import { getApiBaseUrl } from '../constants/environment';
import type { RiftboundCard } from '../types';

// ── Per-card session cache for /api/card-print ───────────────────────────────
// Card print records are immutable within a session (they change only on
// catalog syncs), but the collection route refetched every owned card on each
// mount — a full network round-trip on every bottom-tab change. Caching per
// cardId means a re-mount renders instantly and only genuinely new ids (e.g.
// a card just scanned) hit the network.

const printCache = new Map<string, RiftboundCard>();
/** Dedupe concurrent POSTs for the same id chunk (provider prefetch + page). */
const inflightChunks = new Map<string, Promise<void>>();

const CHUNK = 250;

/**
 * Synchronously split ids into already-cached cards (in input order) and the
 * ids that still need a fetch. Lets callers paint the cached subset
 * immediately while the missing ids load.
 */
export function peekCardPrints(ids: readonly string[]): {
  cards: RiftboundCard[];
  missingIds: string[];
} {
  const cards: RiftboundCard[] = [];
  const missingIds: string[] = [];
  for (const id of ids) {
    const hit = printCache.get(id);
    if (hit) cards.push(hit);
    else missingIds.push(id);
  }
  return { cards, missingIds };
}

function abortError(): Error {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

/** Prefer CDN-cacheable GET when the id list fits a safe URL budget. */
const GET_URL_BUDGET = 1500;

function fetchChunkOnce(chunk: string[]): Promise<void> {
  const sorted = chunk.slice().sort();
  const key = sorted.join('\0');
  const existing = inflightChunks.get(key);
  if (existing) return existing;

  const next = (async () => {
    const idsParam = sorted.join(',');
    const getUrl = `${getApiBaseUrl()}/api/card-print?ids=${encodeURIComponent(idsParam)}`;
    const response =
      getUrl.length <= GET_URL_BUDGET
        ? await fetch(getUrl)
        : await fetch(`${getApiBaseUrl()}/api/card-print`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: chunk }),
          });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = (await response.json()) as { cards: RiftboundCard[] };
    for (const card of data.cards ?? []) {
      printCache.set(card.id, card);
    }
  })().finally(() => {
    inflightChunks.delete(key);
  });

  inflightChunks.set(key, next);
  return next;
}

/**
 * Batch-fetch card print records, serving cached ids from memory and only
 * requesting the rest. Returns cards in input-id order; ids the API doesn't
 * know are silently omitted (matching the previous behaviour).
 * Throws on network/HTTP failure so callers can keep their last good list.
 * Concurrent callers share in-flight chunk requests; `signal` only cancels the
 * wait for this caller (shared fetches keep running for prefetch).
 */
export async function fetchCardPrintsCached(
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<RiftboundCard[]> {
  if (signal?.aborted) throw abortError();

  const { missingIds } = peekCardPrints(ids);

  if (missingIds.length > 0) {
    const chunks: string[][] = [];
    for (let i = 0; i < missingIds.length; i += CHUNK) {
      chunks.push(missingIds.slice(i, i + CHUNK));
    }
    const fetchAll = Promise.all(chunks.map((chunk) => fetchChunkOnce(chunk)));
    if (signal) {
      await new Promise<void>((resolve, reject) => {
        const onAbort = () => {
          signal.removeEventListener('abort', onAbort);
          reject(abortError());
        };
        if (signal.aborted) {
          onAbort();
          return;
        }
        signal.addEventListener('abort', onAbort);
        void fetchAll.then(
          () => {
            signal.removeEventListener('abort', onAbort);
            resolve();
          },
          (err) => {
            signal.removeEventListener('abort', onAbort);
            reject(err);
          },
        );
      });
    } else {
      await fetchAll;
    }
  }

  const out: RiftboundCard[] = [];
  for (const id of ids) {
    const hit = printCache.get(id);
    if (hit) out.push(hit);
  }
  return out;
}

/** Test-only: clear the module cache between tests. */
export function __resetCardPrintCacheForTests(): void {
  printCache.clear();
  inflightChunks.clear();
}
